import { Config } from '@src/core/Config';
import type { Rng } from '@src/core/Rng';

export enum GateSide {
  North = 0,
  East = 1,
  South = 2,
  West = 3,
}

/** Room flavor; also what a gate advertises about the room behind it. */
export enum DoorKind {
  Combat = 0,
  Treasure = 1,
  Hazard = 2,
  Exit = 3,
  /** No room behind this wall: the gate is drawn as solid stone. */
  Sealed = 4,
  /** Champion's Ring: no portals, the boss lands the moment the gates shut. */
  Arena = 5,
  /** Gargoyle Gallery: one portal, a wall full of guns. */
  Gauntlet = 6,
  /** Sanctum: no fight. A heart, a prize, and a breath. */
  Sanctum = 7,
}

export const DOOR_NAMES: Readonly<Record<DoorKind, string>> = {
  [DoorKind.Combat]: 'COMBAT',
  [DoorKind.Treasure]: 'TREASURE VAULT',
  [DoorKind.Hazard]: 'HAZARD PIT',
  [DoorKind.Exit]: 'FLOOR EXIT',
  [DoorKind.Sealed]: 'SEALED',
  [DoorKind.Arena]: "CHAMPION'S RING",
  [DoorKind.Gauntlet]: 'GARGOYLE GALLERY',
  [DoorKind.Sanctum]: 'SANCTUM',
};

/** Rooms that never spend a fight: no portals, no boss. */
export function isRestRoom(door: DoorKind): boolean {
  return door === DoorKind.Sanctum;
}

/** Rooms that do not advance the tier counter (they are a breather, not a step). */
export function isFreeRoom(door: DoorKind): boolean {
  return door === DoorKind.Treasure || door === DoorKind.Sanctum;
}

export const HALF_W = Config.room.width / 2;
export const HALF_H = Config.room.height / 2;

/** Inner playable bounds (walls subtracted). */
export const BOUNDS = {
  minX: -HALF_W + Config.room.wallThickness,
  maxX: HALF_W - Config.room.wallThickness,
  minY: -HALF_H + Config.room.wallThickness,
  maxY: HALF_H - Config.room.wallThickness,
} as const;

export interface GateGeometry {
  side: GateSide;
  x: number;
  y: number;
  /** Half extents of the gate quad. */
  hw: number;
  hh: number;
  /** Direction pointing out of the room. */
  nx: number;
  ny: number;
}

export const GATES: readonly GateGeometry[] = [
  { side: GateSide.North, x: 0, y: HALF_H - Config.room.wallThickness / 2, hw: Config.room.gateWidth / 2, hh: Config.room.wallThickness / 2, nx: 0, ny: 1 },
  { side: GateSide.East, x: HALF_W - Config.room.wallThickness / 2, y: 0, hw: Config.room.wallThickness / 2, hh: Config.room.gateWidth / 2, nx: 1, ny: 0 },
  { side: GateSide.South, x: 0, y: -HALF_H + Config.room.wallThickness / 2, hw: Config.room.gateWidth / 2, hh: Config.room.wallThickness / 2, nx: 0, ny: -1 },
  { side: GateSide.West, x: -HALF_W + Config.room.wallThickness / 2, y: 0, hw: Config.room.wallThickness / 2, hh: Config.room.gateWidth / 2, nx: -1, ny: 0 },
];

const STORAGE_KEY_LAST_PORTALS = 'wa.last_portal_positions';
let memoryLastPortals: Array<[number, number]> = [];

function loadPreviousGamePortals(): Array<[number, number]> {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = window.localStorage.getItem(STORAGE_KEY_LAST_PORTALS);
      if (raw) {
        const parsed = JSON.parse(raw) as Array<[number, number]>;
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    }
  } catch {
    // Storage blocked or unavailable
  }
  return memoryLastPortals;
}

function savePreviousGamePortals(slots: Array<[number, number]>): void {
  memoryLastPortals = slots.slice();
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY_LAST_PORTALS, JSON.stringify(slots));
    }
  } catch {
    // Storage blocked
  }
}

/** Fallback static slots kept for backwards compatibility. */
const PORTAL_SLOTS: ReadonlyArray<readonly [number, number]> = [
  [-11, 5.5],
  [11, -5.5],
  [11, 5.5],
  [-11, -5.5],
  [0, 6.5],
  [0, -6.5],
  [-6, 0],
  [6, 0],
];

export function portalSlot(i: number): readonly [number, number] {
  return PORTAL_SLOTS[i % PORTAL_SLOTS.length];
}

/**
 * Procedurally generate portal placements for a room.
 * Ensures:
 * 1. Safe distance from walls (allowing imp burst ring room).
 * 2. Gate corridors are clear so doorways are never blocked.
 * 3. Clear radius around player entry point (and center during cold open).
 * 4. Mutual distance between portals via Mitchell's best-candidate sampling.
 * 5. Distinctly new positions that avoid repeating where portals spawned in previous games.
 */
export function generatePortalSlots(
  count: number,
  rng: Rng,
  playerX = 0,
  playerY = 0,
  isColdOpen = false
): Array<[number, number]> {
  const n = Math.max(1, Math.min(Config.pools.spawners, count));
  const xMin = BOUNDS.minX + 2.6; // -12.8
  const xMax = BOUNDS.maxX - 2.6; //  12.8
  const yMin = BOUNDS.minY + 2.2; // -6.2
  const yMax = BOUNDS.maxY - 2.2; //  6.2

  const prevGamePortals = isColdOpen ? loadPreviousGamePortals() : [];
  const placed: Array<[number, number]> = [];

  let targetMinDist = 4.8;
  let prevDistAvoid = 3.5;

  const isGateBlocked = (x: number, y: number): boolean => {
    if (y > 4.2 && Math.abs(x) < 3.0) return true; // North gate corridor
    if (y < -4.2 && Math.abs(x) < 3.0) return true; // South gate corridor
    if (x > 10.0 && Math.abs(y) < 3.0) return true; // East gate corridor
    if (x < -10.0 && Math.abs(y) < 3.0) return true; // West gate corridor
    return false;
  };

  const isTooCloseToPlayer = (x: number, y: number): boolean => {
    if (isColdOpen) {
      if (Math.hypot(x, y) < 4.0) return true;
    } else {
      if (Math.hypot(x - playerX, y - playerY) < 4.2) return true;
    }
    return false;
  };

  for (let p = 0; p < n; p++) {
    let bestCandidate: [number, number] | null = null;
    let bestScore = -1;
    let attempts = 0;
    let currentMinDist = targetMinDist;
    let currentPrevAvoid = prevDistAvoid;

    while (bestCandidate === null && attempts < 120) {
      attempts++;
      // Sample candidate points
      for (let c = 0; c < 24; c++) {
        const cx = rng.range(xMin, xMax);
        const cy = rng.range(yMin, yMax);

        if (isGateBlocked(cx, cy)) continue;
        if (isTooCloseToPlayer(cx, cy)) continue;

        // Check against previous game's opening portals
        if (currentPrevAvoid > 0 && prevGamePortals.length > 0) {
          let tooCloseToPrev = false;
          for (let i = 0; i < prevGamePortals.length; i++) {
            if (Math.hypot(cx - prevGamePortals[i][0], cy - prevGamePortals[i][1]) < currentPrevAvoid) {
              tooCloseToPrev = true;
              break;
            }
          }
          if (tooCloseToPrev) continue;
        }

        // Check against already placed portals in this room
        let minOtherDist = 999;
        let tooCloseToPlaced = false;
        for (let i = 0; i < placed.length; i++) {
          const d = Math.hypot(cx - placed[i][0], cy - placed[i][1]);
          if (d < currentMinDist) {
            tooCloseToPlaced = true;
            break;
          }
          if (d < minOtherDist) minOtherDist = d;
        }
        if (tooCloseToPlaced) continue;

        // Candidate valid! Score by minimum distance to placed portals or player
        const score = placed.length > 0 ? minOtherDist : Math.hypot(cx - playerX, cy - playerY);
        if (score > bestScore) {
          bestScore = score;
          bestCandidate = [cx, cy];
        }
      }

      // If we couldn't find a candidate with current constraints, progressively relax
      if (bestCandidate === null) {
        if (currentMinDist > 3.2) {
          currentMinDist -= 0.4;
        } else if (currentPrevAvoid > 1.8) {
          currentPrevAvoid -= 0.6;
        } else {
          currentPrevAvoid = 0;
        }
      }
    }

    if (bestCandidate !== null) {
      placed.push(bestCandidate);
    } else {
      // Fallback: pick a pseudo-random jittered slot not in center
      const fallbackAngle = (p / n) * Math.PI * 2 + rng.range(-0.3, 0.3);
      const fallbackDist = rng.range(5.5, 8.5);
      const fx = Math.max(xMin, Math.min(xMax, Math.cos(fallbackAngle) * fallbackDist));
      const fy = Math.max(yMin, Math.min(yMax, Math.sin(fallbackAngle) * fallbackDist));
      placed.push([fx, fy]);
    }
  }

  if (isColdOpen && placed.length > 0) {
    savePreviousGamePortals(placed);
  }

  return placed;
}

/**
 * Portals a room plants before the difficulty budget. Zero for rooms that fight without
 * portals (Champion's Ring) or do not fight at all (Sanctum); the caller must respect zero.
 */
export function spawnerCountForTier(tier: number, door: DoorKind): number {
  if (door === DoorKind.Arena || door === DoorKind.Sanctum) return 0;
  if (door === DoorKind.Gauntlet) return 1;
  const base = 2 + Math.floor(tier / 2);
  const bonus = door === DoorKind.Hazard ? 2 : door === DoorKind.Treasure ? -1 : door === DoorKind.Exit ? 1 : 0;
  return Math.max(1, Math.min(Config.pools.spawners, base + bonus));
}

/** Inward-facing spawn point just inside a gate (used when entering from the opposite side). */
export function gateEntryPoint(side: GateSide, out: Float32Array): void {
  const g = GATES[side];
  out[0] = g.x - g.nx * 2.2;
  out[1] = g.y - g.ny * 2.2;
}

export function isInCorner(x: number, y: number): boolean {
  const s = Config.room.cornerRegionSize;
  const nearX = x < BOUNDS.minX + s || x > BOUNDS.maxX - s;
  const nearY = y < BOUNDS.minY + s || y > BOUNDS.maxY - s;
  return nearX && nearY;
}

export function isOutside(x: number, y: number, margin: number): boolean {
  return x < BOUNDS.minX - margin || x > BOUNDS.maxX + margin || y < BOUNDS.minY - margin || y > BOUNDS.maxY + margin;
}

/** Fight rooms get mines; rest rooms stay clean. */
export function mineCountForRoom(tier: number, door: DoorKind, floor: number): number {
  if (door === DoorKind.Sanctum || door === DoorKind.Treasure) return 0;
  const c = Config.mines;
  let n = c.countBase + Math.floor(Math.max(0, tier - 1) / c.countEveryTiers) + Math.floor(Math.max(0, floor) * c.countPerFloor);
  if (door === DoorKind.Hazard) n += c.hazardBonus;
  if (door === DoorKind.Gauntlet) n += c.gauntletBonus;
  return Math.max(c.countMin, Math.min(c.countMax, n));
}

/**
 * Scatter mines across the floor, clear of gates, the entry tile, portals, and
 * each other so the room stays pathable.
 */
export function generateMineSlots(
  count: number,
  rng: Rng,
  playerX: number,
  playerY: number,
  avoid: ReadonlyArray<readonly [number, number]>,
): Array<[number, number]> {
  const c = Config.mines;
  const n = Math.max(0, Math.min(c.countMax, count));
  const placed: Array<[number, number]> = [];
  if (n <= 0) return placed;

  const xMin = BOUNDS.minX + 1.6;
  const xMax = BOUNDS.maxX - 1.6;
  const yMin = BOUNDS.minY + 1.4;
  const yMax = BOUNDS.maxY - 1.4;

  const isGateBlocked = (x: number, y: number): boolean => {
    if (y > 4.2 && Math.abs(x) < 3.0) return true;
    if (y < -4.2 && Math.abs(x) < 3.0) return true;
    if (x > 10.0 && Math.abs(y) < 3.0) return true;
    if (x < -10.0 && Math.abs(y) < 3.0) return true;
    return false;
  };

  const blocked = (x: number, y: number, spacing: number): boolean => {
    if (isGateBlocked(x, y)) return true;
    if (Math.hypot(x - playerX, y - playerY) < c.entryClear) return true;
    for (let i = 0; i < avoid.length; i++) {
      if (Math.hypot(x - avoid[i][0], y - avoid[i][1]) < c.portalClear) return true;
    }
    for (let i = 0; i < placed.length; i++) {
      if (Math.hypot(x - placed[i][0], y - placed[i][1]) < spacing) return true;
    }
    return false;
  };

  for (let p = 0; p < n; p++) {
    let best: [number, number] | null = null;
    let bestScore = -1;
    let spacing: number = c.minSpacing;
    for (let relax = 0; relax < 6 && best === null; relax++) {
      for (let s = 0; s < 28; s++) {
        const cx = rng.range(xMin, xMax);
        const cy = rng.range(yMin, yMax);
        if (blocked(cx, cy, spacing)) continue;
        let nearest = 99;
        for (let i = 0; i < placed.length; i++) {
          const d = Math.hypot(cx - placed[i][0], cy - placed[i][1]);
          if (d < nearest) nearest = d;
        }
        const fromPlayer = Math.hypot(cx - playerX, cy - playerY);
        const score = placed.length > 0 ? nearest + fromPlayer * 0.15 : fromPlayer;
        if (score > bestScore) {
          bestScore = score;
          best = [cx, cy];
        }
      }
      if (best === null) spacing = Math.max(2.2, spacing - 0.35);
    }
    if (best) placed.push(best);
  }
  return placed;
}
